"use client";

import { motion } from "framer-motion";
import {
  Building2,
  CalendarDays,
  Euro,
  GraduationCap,
  History,
  Landmark,
  Languages,
  ListChecks,
} from "lucide-react";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { semesterLabel, type University } from "@/lib/universities";
import type { UniversityDetail } from "@/lib/university-details";

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Landmark;
  label: string;
  value: string;
}) {
  return (
    <motion.div
      variants={fadeUp}
      className="card-flat flex items-start gap-3.5 bg-white/60 p-5"
    >
      <span className="w-10 h-10 shrink-0 rounded-xl bg-accent-soft text-accent flex items-center justify-center">
        <Icon size={18} aria-hidden="true" />
      </span>
      <span className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs font-bold uppercase tracking-wider text-ink-faint">
          {label}
        </span>
        <span className="text-sm font-semibold text-ink leading-snug">{value}</span>
      </span>
    </motion.div>
  );
}

export default function UniOverview({
  university,
  detail,
}: {
  university: University;
  detail?: UniversityDetail;
}) {
  const stats = [
    { icon: Landmark, label: "Einrichtung", value: university.type },
    { icon: Building2, label: "Bundesland", value: university.state },
    { icon: CalendarDays, label: "Studienbeginn", value: semesterLabel(university.semesters) },
    { icon: Languages, label: "Unterrichtssprache", value: university.language },
    { icon: GraduationCap, label: "Studiengänge", value: university.subjects.join(", ") },
    {
      icon: Euro,
      label: "Studiengebühren",
      value: university.tuition ?? "Keine (staatlich)",
    },
    {
      icon: ListChecks,
      label: "Auswahlverfahren",
      value:
        university.type === "Staatlich" ? "ABQ · AdH · ZEQ" : "Eigenes Verfahren, ohne NC",
    },
    ...(detail?.founded
      ? [{ icon: History, label: "Gegründet", value: String(detail.founded) }]
      : []),
  ];

  return (
    <motion.section
      variants={stagger(0.06)}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      aria-labelledby="overview-heading"
    >
      <motion.h2
        variants={fadeUp}
        id="overview-heading"
        className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink mb-6"
      >
        Auf einen Blick
      </motion.h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>
    </motion.section>
  );
}
